// src/pages/ActiveDirectoryAudits.jsx - Active Directory Audit List & Upload
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  FolderOpen as FolderIcon
} from '@mui/icons-material';
import { adAuditApi, clientsApi } from '../services/api';
import { toast } from 'react-toastify';

const ActiveDirectoryAudits = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    client_id: '',
    audit_name: '',
    audit_date: new Date().toISOString().split('T')[0],
    files: []
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, auditId: null });
  const [filterClient, setFilterClient] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [filterClient]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = filterClient ? { client_id: filterClient } : {};
      const [auditsRes, clientsRes] = await Promise.all([
        adAuditApi.getAudits(params),
        clientsApi.getAll()
      ]);
      setAudits(Array.isArray(auditsRes.data) ? auditsRes.data : []);
      const clientData = clientsRes.data;
      setClients(Array.isArray(clientData) ? clientData : (clientData.clients || []));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load audits');
    } finally {
      setLoading(false);
    }
  };

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['docx', 'csv', 'txt', 'xlsx', 'xls', 'pdf'].includes(ext);
    });
    if (valid.length !== selected.length) {
      toast.warning('Some files were skipped. Accepted: .docx, .xlsx, .xls, .csv, .txt, .pdf');
    }
    // Accumulate files across multiple selections; deduplicate by name
    setUploadData(prev => {
      const existingNames = new Set(prev.files.map(f => f.name));
      const newFiles = valid.filter(f => !existingNames.has(f.name));
      if (newFiles.length < valid.length) {
        toast.info(`${valid.length - newFiles.length} duplicate file(s) skipped.`);
      }
      return { ...prev, files: [...prev.files, ...newFiles] };
    });
    // Reset the input so the same file can be selected again after removal
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (fileName) => {
    setUploadData(prev => ({ ...prev, files: prev.files.filter(f => f.name !== fileName) }));
  };

  const handleUpload = async () => {
    if (!uploadData.client_id || !uploadData.audit_name || !uploadData.files.length) {
      setError('Please fill in all required fields and select at least one file');
      return;
    }
    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      uploadData.files.forEach(f => formData.append('files', f));
      formData.append('client_id', uploadData.client_id);
      formData.append('audit_name', uploadData.audit_name);
      formData.append('audit_date', uploadData.audit_date);
      await adAuditApi.uploadAudit(formData);
      toast.success('Files uploaded successfully! Click "Analyze with AI" to generate findings.');
      setUploadDialogOpen(false);
      setUploadData({ client_id: '', audit_name: '', audit_date: new Date().toISOString().split('T')[0], files: [] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adAuditApi.deleteAudit(deleteConfirm.auditId);
      toast.success('Audit deleted');
      setDeleteConfirm({ open: false, auditId: null });
      fetchData();
    } catch {
      toast.error('Failed to delete audit');
    }
  };

  const getStatusChip = (status) => {
    const map = {
      processing: { label: 'Pending Analysis', color: 'warning' },
      analyzed: { label: 'Analyzed', color: 'primary' },
      report_generated: { label: 'Report Generated', color: 'success' },
      error: { label: 'Error', color: 'error' }
    };
    const s = map[status] || { label: status, color: 'default' };
    return <Chip label={s.label} color={s.color} size="small" />;
  };

  const getRiskColor = (score) => {
    if (score >= 60) return '#e74c3c';
    if (score >= 30) return '#e67e22';
    return '#27ae60';
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
            Active Directory Audits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and analyze Active Directory security assessment documents
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
        >
          New Audit
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filter by Client */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Client</InputLabel>
            <Select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              label="Filter by Client"
            >
              <MenuItem value="">All Clients</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#6B3FA0' }} />
        </Box>
      ) : audits.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <FolderIcon sx={{ fontSize: 64, color: '#E5D5F5', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No AD Audits Yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload Active Directory assessment documents (DOCX, CSV, or TXT) to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
            >
              Upload First Audit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F3EEFA' }}>
              <TableRow>
                <TableCell><strong>Audit Name</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell align="center"><strong>Findings</strong></TableCell>
                <TableCell align="center"><strong>Critical</strong></TableCell>
                <TableCell align="center"><strong>High</strong></TableCell>
                <TableCell align="center"><strong>Risk Score</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => (
                <TableRow
                  key={audit.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/active-directory-audits/${audit.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{audit.audit_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        try {
                          const fns = JSON.parse(audit.uploaded_filenames || '[]');
                          return fns.length === 1 ? fns[0] : `${fns.length} files`;
                        } catch { return audit.uploaded_filenames || ''; }
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell>{audit.client_name}</TableCell>
                  <TableCell>
                    {audit.audit_date ? new Date(audit.audit_date).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {audit.total_findings || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {audit.critical_count > 0 ? (
                      <Chip
                        label={audit.critical_count}
                        size="small"
                        sx={{ bgcolor: '#e74c3c', color: 'white', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {audit.high_count > 0 ? (
                      <Chip
                        label={audit.high_count}
                        size="small"
                        sx={{ bgcolor: '#e67e22', color: 'white', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {audit.overall_score > 0 ? (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: getRiskColor(audit.overall_score) }}
                      >
                        {parseFloat(audit.overall_score).toFixed(1)}/100
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{getStatusChip(audit.status)}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/active-directory-audits/${audit.id}`)}
                      title="View"
                      sx={{ color: '#6B3FA0' }}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteConfirm({ open: true, auditId: audit.id })}
                      title="Delete"
                      sx={{ color: '#e74c3c' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#4A2B70', fontWeight: 700 }}>
          Upload Active Directory Audit
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Alert severity="info" sx={{ mb: 2 }}>
            Upload one or more documents from your AD security assessment. Accepted formats: <strong>.docx</strong>, <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>, <strong>.txt</strong>, <strong>.pdf</strong>
            <br /><br />
            You can upload any relevant files, but common files to review are: <strong>Excel_Export.xlsx</strong>, <strong>Password_Policies_Summary.docx</strong>, <strong>Share_Permission_Report_Excel.xlsx</strong>
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Client *</InputLabel>
            <Select
              value={uploadData.client_id}
              onChange={(e) => setUploadData(prev => ({ ...prev, client_id: e.target.value }))}
              label="Client *"
            >
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Audit Name *"
            value={uploadData.audit_name}
            onChange={(e) => setUploadData(prev => ({ ...prev, audit_name: e.target.value }))}
            placeholder="e.g. Q1 2026 Active Directory Audit"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Audit Date *"
            type="date"
            value={uploadData.audit_date}
            onChange={(e) => setUploadData(prev => ({ ...prev, audit_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{ borderColor: '#6B3FA0', color: '#6B3FA0', py: 1.5, mb: 1 }}
          >
            {uploadData.files.length > 0 ? 'Add More Files' : 'Select Files (Multiple Allowed)'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept=".docx,.xlsx,.xls,.csv,.txt,.pdf"
              onChange={handleFilesChange}
            />
          </Button>

          {uploadData.files.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#6B3FA0', fontWeight: 600 }}>
                  {uploadData.files.length} file{uploadData.files.length !== 1 ? 's' : ''} selected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#e74c3c', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setUploadData(prev => ({ ...prev, files: [] }))}
                >
                  Clear all
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {uploadData.files.map((f) => (
                  <Chip
                    key={f.name}
                    label={f.name}
                    size="small"
                    onDelete={() => handleRemoveFile(f.name)}
                    sx={{ bgcolor: '#F3EEFA', maxWidth: 260 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Uploading and extracting text...
              </Typography>
              <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#6B3FA0' } }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadData.client_id || !uploadData.audit_name || !uploadData.files.length}
            sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, auditId: null })}>
        <DialogTitle>Delete Audit?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This will permanently delete the audit and all associated findings and analysis. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, auditId: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveDirectoryAudits;
